import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { getClientById, IClientData, IClientUID } from "../../api/clients";
import { appRef } from "../../app";
import { HiSwitchVertical } from "react-icons/hi";
import { getLocale, getString } from "../../locale";
import Loader from "../../components/loader";
import classNames from "classnames";
import { INTERVAL_OPTIONS, IntervalOption, PumpState, RegularState, RegularStateFunc } from "./0x0001/systeminfo.tab";
import { getClientSystemInfo, ISystemInfo } from "../../api/0x0001/systeminfo";
import { readableTime } from "../../utils/time";
import { format } from "date-fns";
import { mvToBar } from "../../utils/piezo";
import { ProgramMode } from "../../api/0x0001/settings";
import { mvToPercent } from "../../utils/number";
import { mvToTemp } from "../../utils/ntc";
import { toast } from "react-toastify";
import { FaCog, FaWifi, FaSdCard, FaKeyboard, FaRegUser, FaClock } from 'react-icons/fa'
import { Window } from "../../window";
import SettingsTab from "./0x0001/settings.tab";
import WifiConfigTab from "./0x0001/wificonfig.tab";
import OtaTab from "./0x0001/ota.tab";
import SystemActionsTab from "./0x0001/systemactions.tab";
import CustomerDataTab from "./0x0001/customerdata.tab";
import DateTimeTab from "./0x0001/datetime.tab";

type Params = {
  clientIds: IClientUID
}

type Props = {}

type State = {
  clients?: IClientData[]
  systemInfoArr: ISystemInfo[]
  intervalOption: IntervalOption
}

export class MultiClient extends Component<RouteComponentProps<Params>> {
  mounted: boolean = false

  state: State = {
    intervalOption: INTERVAL_OPTIONS[1],
    systemInfoArr: [],
  }

  clientIds: IClientUID[]

  constructor(props: RouteComponentProps<Params>) {
    super(props)
    this.clientIds = this.props.match.params.clientIds.split(',') as IClientUID[]
  }

  setState: Component<Props, State>['setState'] = (state, callback) => {
    if (!this.mounted) return
    return super.setState(state as State, callback)
  }

  componentDidMount = async () => {
    this.mounted = true
    // const clients = await Promise.all(this.clientIds.map(clientId => await getClientById(clientId).data))
    // fix the above
    const clients = await Promise.all(this.clientIds.map(async clientId => (await getClientById(clientId)).data))
    this.setState({ clients })

    appRef.setHeader(
      <HiSwitchVertical className="me-2" size={28} />,
      () => getString('pagelabels.multiclient'),
    )

    this.loadData()
  }

  componentWillUnmount = () => {
    this.mounted = false
  }

  async loadData() {
    if (!this.mounted) return
    try {
      const systemInfoResults = await Promise.allSettled(this.state.clients!.map(async client => (await getClientSystemInfo(client.id)).data))

      const systemInfoArr = systemInfoResults.map(result => {
        if (result.status === 'fulfilled') return result.value
        return undefined
      }) as ISystemInfo[]

      this.setState({ systemInfoArr })

    } catch (err) {
      toast.error((err as Error).message)
      console.error(err)
    }
    setTimeout(this.loadData.bind(this), this.state.intervalOption.ms)
  }

  render() {
    return (
      <div>
        {this.renderTable()}
        {this.renderIntervalSelect()}
      </div>
    );
  }

  renderTable = () => {
    const { clients, systemInfoArr } = this.state
    if (!clients) return null

    return <div className="border-2 border-b-0 border-gray-500">
      {clients.map((client, index) => (
        <div key={index}>
          {this.renderTableRow(client, systemInfoArr[index])}
        </div>
      ))}
    </div>
  }

  renderIntervalSelect() {
    const { intervalOption } = this.state
    return (
      <div className="mt-10 w-1/2 grid grid-cols-3 gap-8">
        <div className="flex items-center dark:text-white">{getString('system_info.update_interval')}:</div>
        {INTERVAL_OPTIONS.map((option, i) => (
          <div
            key={i}
            onClick={() => this.setState({ intervalOption: option })}
            className={classNames(
              'flex items-center p-4 bg-white dark:bg-gray-700 dark:text-white shadow-md rounded-xl',
              intervalOption.ms === option.ms ? 'bg-gray-200 dark:bg-opacity-40 cursor-not-allowed' : 'hover-grow',
            )}
          >
            {option.label()}
          </div>
        ))}
      </div>
    )
  }

  renderActions(client: IClientData) {
    return (
      <div className="flex flex-col justify-center items-center text-black dark:text-white w-12 space-y-3">
        <FaCog
          className="cursor-pointer hover:text-gray-500 dark:hover:text-gray-400"
          onClick={() => new Window().fire({
            title: getString('client.settings'),
            body: <SettingsTab clientId={client.id} />,
            confirmButtonColor: "blue",
          })}
        />
        <FaWifi
          className="cursor-pointer hover:text-gray-500 dark:hover:text-gray-400"
          onClick={() => new Window().fire({
            title: getString('client.wifi_config'),
            body: <WifiConfigTab clientId={client.id} />,
            confirmButtonColor: "blue",
          })}
        />
        <FaClock
          className="cursor-pointer hover:text-gray-500 dark:hover:text-gray-400"
          onClick={() => new Window().fire({
            title: getString('client.datetime'),
            body: <DateTimeTab clientId={client.id} />,
            confirmButtonColor: "blue",
          })}
        />
        <FaRegUser
          className="cursor-pointer hover:text-gray-500 dark:hover:text-gray-400"
          onClick={() => new Window().fire({
            title: getString('client.customer_data'),
            body: <CustomerDataTab clientId={client.id} />,
            confirmButtonColor: "blue",
          })}
        />
        <FaKeyboard
          className="cursor-pointer hover:text-gray-500 dark:hover:text-gray-400"
          onClick={() => new Window().fire({
            title: getString('client.system_actions'),
            body: <SystemActionsTab clientId={client.id} />,
            confirmButtonColor: "blue",
          })}
        />
        <FaSdCard
          className="cursor-pointer hover:text-gray-500 dark:hover:text-gray-400"
          onClick={() => new Window().fire({
            title: getString('client.ota'),
            body: <OtaTab clientId={client.id} />,
            confirmButtonColor: "blue",
          })}
        />
      </div>
    )
  }

  renderTableRow(client: IClientData, systemInfo?: ISystemInfo) {
    if (!systemInfo) return <Loader />

    return (
      <div className="mb-0 flex justify-center items-stretch w-full p-0 border-b-2 border-gray-500">
        {this.renderActions(client)}

        {this.renderBlock(
          systemInfo,
          undefined,
          systemInfo && (
            <>
              <div className="text-sm font-bold">{client.machine?.name}</div>
              <div className="mt-1 text-sm font-bold">{client.id}</div>

              <div>
                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.epoch')}</div>
                <div style={{ direction: 'ltr' }}>{format(new Date(Number(systemInfo.epoch) * 1000), 'dd/MM/yyyy HH:mm:ss')}</div>
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.uptime')}</div>
                {readableTime(Number(systemInfo.uptime), true)}
              </div>
              {/* <div>
                <div>{getString('system_info.epoch')}</div>
                {readableMemory(Number(systemInfo.freeHeap))}
              </div> */}
              {/* <div className="mt-1 flex justify-center space-x-5">
                <div>
                  <div className="text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.vdet')}</div>
                  <div className="text-xs">
                    {systemInfo.vdet / 1000}
                    {getString('common.v')}
                  </div>
                </div>
                <div>
                  <div className="text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.iprop')}</div>
                  <div className="text-xs">
                    {systemInfo.iprop / 1000}
                    {getString('common.v')}
                  </div>
                </div>
              </div> */}
            </>
          ),
        )}

        {(!systemInfo?.ignoreH2oPiezo || !systemInfo?.ignoreCo2Piezo) && this.renderBlock(
          systemInfo,
          getString('system_info.piezo1'),
          systemInfo && (
            <>
              <div className={classNames(systemInfo.ignoreH2oPiezo && 'line-through')}>
                {systemInfo.piezo1 < 3050 ?
                  <div className="text-lg" style={{ direction: getLocale() === 'he' ? 'rtl' : 'ltr' }}>
                    {mvToBar(systemInfo.piezo1)} {getString('common.bar')}
                  </div> :
                  <div className="text-sm text-red-500">
                    {getString('common.disconnected')}
                  </div>
                }
                {/* <div className="mt-1 text-xs">
                  {systemInfo.piezo1 / 1000}
                  {getString('common.v')}
                </div> */}
              </div>

              <div className="mt-1 text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.piezo2')}</div>
              {systemInfo.piezo2 < 3050 ?
                <div
                  className={classNames('mt-1 text-lg', systemInfo.ignoreCo2Piezo && 'line-through')}
                  style={{ direction: getLocale() === 'he' ? 'rtl' : 'ltr' }}
                >
                  {mvToBar(systemInfo.piezo2)} {getString('common.bar')}
                </div> :
                <div className="mt-1 text-sm text-red-500">
                  {getString('common.disconnected')}
                </div>
              }
              {/* <div className={classNames('mt-1 text-xs', systemInfo.ignoreCo2Piezo && 'line-through')}>
                {systemInfo.piezo2 / 1000}
                {getString('common.v')}
              </div> */}
            </>
          ),
          !!systemInfo,
        )}

        {systemInfo?.progMode === ProgramMode.IceBank &&
          this.renderBlock(
            systemInfo,
            getString('system_info.ice_sense'),
            <>
              <div className="text-lg">{mvToPercent(systemInfo.iceSense)}%</div>
              {/* <div className="mt-1 text-xs">
                {systemInfo.iceSense / 1000}
                {getString('common.v')}
              </div> */}

              <div className="mt-1">
                <div className="text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.ice_sense')}</div>
                <div className="mt-1 text-lg">{mvToPercent(systemInfo.rawIceSense)}%</div>
                {/* <div className="mt-1 text-xs">
                  {systemInfo.rawIceSense / 1000}
                  {getString('common.v')}
                </div> */}
              </div>
            </>,
          )}

        {this.renderBlock(
          systemInfo,
          <div className="flex justify-center">
            <div className="me-2 flex flex-col items-center">{RegularState[Number(systemInfo.compressorState)]}</div>
            {getString('system_info.cooling_unit')}
          </div>,
          systemInfo && (
            <>
              <div className="mt-1">
                <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.thermostat')}
                </div>
                <div className="mt-0 text-lg" style={{ direction: 'ltr' }}>
                  {systemInfo.ntc1 < 3050 ?
                    <>
                      {mvToTemp(systemInfo.ntc1)[0]} {getString('common.celsius')}
                    </> :
                    <div className="text-sm text-red-500">
                      {getString('common.disconnected')}
                    </div>
                  }
                </div>
                {/* <div className="mt-1 text-xs">
                  {systemInfo.ntc1 / 1000}
                  {getString('common.v')}
                </div> */}
              </div>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.active_time')}
                </div>
                <div>{readableTime(systemInfo.compressorActiveTime, true)}</div>
              </div>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.inactive_time')}
                </div>
                <div>{readableTime(systemInfo.compressorInactiveTime, true)}</div>
              </div>
            </>
          ),
        )}

        {!systemInfo?.liqLevDisabled &&
          this.renderBlock(
            systemInfo,
            <div className="flex justify-center">
              <div className="me-2 flex flex-col items-center">{PumpState[Number(systemInfo.pumpState)]}</div>
              {getString('system_info.soda_unit')}
            </div>,
            systemInfo && (
              <>
                <div className="text-sm mt-1">
                  <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                  <div>{readableTime(systemInfo.pumpActiveTime, true)}</div>
                </div>
                <div className="text-sm mt-1">
                  <div className="text-gray-600 dark:text-gray-400 font-semibold">
                    {getString('common.inactive_time')}
                  </div>
                  <div>{readableTime(systemInfo.pumpInactiveTime, true)}</div>
                </div>

                <div className="text-base mt-1">
                  <div className="mb-2 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.liquid_level')}</div>
                  <div className="flex justify-center items-center">
                    {!systemInfo.oneLiqLev && (
                      <div className="px-3">{RegularStateFunc[Number(systemInfo.liqH)](getString('system_info.liquid_high'))}</div>
                    )}
                    <div className="px-3">
                      {RegularStateFunc[Number(systemInfo.liqL)](getString('system_info.liquid_low'))}
                    </div>
                  </div>
                </div>
              </>
            ),
          )}

        {systemInfo?.fanEnabled && this.renderBlock(
          systemInfo,
          <div className="flex justify-center">
            <div className="me-2 flex flex-col items-center">{RegularState[Number(systemInfo.fanState)]}</div>
            {getString('system_info.ambient_unit')}
          </div>,
          systemInfo && (
            <>
              <div className="mt-1">
                <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.thermostat')}
                </div>
                <div className="mt-0 text-lg" style={{ direction: 'ltr' }}>
                  {systemInfo.ntc2 < 3050 ?
                    <>
                      {mvToTemp(systemInfo.ntc2)[0]} {getString('common.celsius')}
                    </> :
                    <div className="text-sm text-red-500">
                      {getString('common.disconnected')}
                    </div>
                  }
                </div>
                {/* <div className="mt-1 text-xs">
                  {systemInfo.ntc2 / 1000}
                  {getString('common.v')}
                </div> */}
              </div>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.active_time')}
                </div>
                <div>{readableTime(systemInfo.fanActiveTime, true)}</div>
              </div>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.inactive_time')}
                </div>
                <div>{readableTime(systemInfo.fanInactiveTime, true)}</div>
              </div>
            </>
          ),
        )}

        {systemInfo?.circPumpEnabled && this.renderBlock(
          systemInfo,
          <div className="flex justify-center">
            <div className="me-2 flex flex-col items-center">{RegularState[Number(systemInfo.circPumpState)]}</div>
            {getString('system_info.circulation_pump')}
          </div>,
          systemInfo && (
            <>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.active_time')}
                </div>
                <div>{readableTime(systemInfo.circPumpActiveTime, true)}</div>
              </div>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">
                  {getString('common.inactive_time')}
                </div>
                <div>{readableTime(systemInfo.circPumpInactiveTime, true)}</div>
              </div>
            </>
          ),
        )}

        {systemInfo?.boilerEnabled &&
          this.renderBlock(
            systemInfo,
            <div className="flex justify-center">
              <div className="me-2 flex flex-col items-center">{RegularState[Number(systemInfo.boilerState)]}</div>
              {getString('system_info.boiler_state')}
            </div>,
            <>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.boiler_active_time')}</div>
                <div>{readableTime(systemInfo.boilerActiveTime, true)}</div>
              </div>
              <div className="text-sm mt-1">
                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.boiler_inactive_time')}</div>
                <div>{readableTime(systemInfo.boilerInactiveTime, true)}</div>
              </div>
            </>,
          )}

        {this.renderBlock(
          systemInfo,
          getString('system_info.rel_sols'),
          systemInfo && (
            <div className="flex flex-row flex-wrap justify-center space-x-3">
              <div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol1State)](String(1))}</div>
                </div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol2State)](String(2))}</div>
                </div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol3State)](String(3))}</div>
                </div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol4State)](String(4))}</div>
                </div>
              </div>
              <div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol5State)](String(5))}</div>
                </div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol6State)](String(6))}</div>
                </div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol7State)](String(7))}</div>
                </div>
                <div className="text-sm mt-1 flex justify-center">
                  <div className="ms-1">{RegularStateFunc[Number(systemInfo.relSol8State)](String(8))}</div>
                </div>
              </div>
            </div>
          ),
        )}
      </div>
    )
  }

  renderBlock(systemInfo: ISystemInfo, label: any, value: any, ltr = false, message?: any, size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl') {
    return (
      <div className={classNames('mx-3 py-1 w-40 text-center bg-gray-100 dark:bg-gray-900 flex flex-col items-cente', ltr && 'ltr')}>
        {label && <div className="p-0 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{label}</div>}
        <div className={classNames(`pt-2 text-black dark:text-white font-bold text-${size || 'sm'} leading-7`)}>
          {this.renderValue(systemInfo, value)} {message}
        </div>
      </div>
    )
  }

  renderValue(systemInfo: ISystemInfo, value: any) {
    if (systemInfo === undefined) return <Loader />
    return value
  }
}